/**
 * Error handling utilities for consistent error management
 */

/**
 * Error types enum
 * @enum {string}
 */
export const ErrorTypes = {
  // Authentication and authorization errors
  AUTH: 'auth',
  UNAUTHORIZED: 'unauthorized',
  FORBIDDEN: 'forbidden',
  
  // Data errors
  DATA_NOT_FOUND: 'data_not_found',
  DATA_INVALID: 'data_invalid',
  DATA_LOADING: 'data_loading',
  DATA_SAVING: 'data_saving',
  
  // Input validation errors
  INVALID_INPUT: 'invalid_input',
  MISSING_REQUIRED: 'missing_required',
  
  // Network errors
  NETWORK: 'network',
  SERVER_ERROR: 'server_error',
  TIMEOUT: 'timeout',
  
  // Trial access errors
  TRIAL_LIMIT: 'trial_limit',
  TRIAL_EXPIRED: 'trial_expired',
  IP_LIMITED: 'ip_limited',
  
  // Generic errors
  UNKNOWN: 'unknown'
};

/**
 * Create an error with a specific type
 * @param {string} type - Error type from ErrorTypes enum
 * @param {string} message - Error message
 * @param {Object} [details] - Additional error details
 * @returns {Error} Error object with type property
 */
export function createErrorWithType(type, message, details = {}) {
  const error = new Error(message);
  error.type = type;
  error.details = details;
  return error;
}

/**
 * Get a user-friendly error message based on error type
 * @param {Error} error - Error object
 * @returns {string} User-friendly error message
 */
export function getUserFriendlyErrorMessage(error) {
  // If the error already has a message, use it
  if (error.message) {
    return error.message;
  }
  
  // Default messages based on error type
  switch (error.type) {
    case ErrorTypes.AUTH:
      return 'Error de autenticación. Por favor, inicia sesión de nuevo.';
    
    case ErrorTypes.UNAUTHORIZED:
      return 'No tienes autorización para realizar esta acción.';
    
    case ErrorTypes.FORBIDDEN:
      return 'No tienes permisos para acceder a este recurso.';
    
    case ErrorTypes.DATA_NOT_FOUND:
      return 'No se encontraron los datos solicitados.';
    
    case ErrorTypes.DATA_INVALID:
      return 'Los datos proporcionados no son válidos.';
    
    case ErrorTypes.DATA_LOADING:
      return 'Error al cargar los datos. Por favor, intenta de nuevo.';
    
    case ErrorTypes.DATA_SAVING:
      return 'Error al guardar los datos. Por favor, intenta de nuevo.';
    
    case ErrorTypes.INVALID_INPUT:
      return 'La información proporcionada no es válida.';
    
    case ErrorTypes.MISSING_REQUIRED:
      return 'Faltan campos obligatorios.';
    
    case ErrorTypes.NETWORK:
      return 'Error de conexión. Por favor, verifica tu conexión a internet.';
    
    case ErrorTypes.SERVER_ERROR:
      return 'Error en el servidor. Por favor, intenta de nuevo más tarde.';
    
    case ErrorTypes.TIMEOUT:
      return 'La solicitud ha tardado demasiado tiempo. Por favor, intenta de nuevo.';
    
    case ErrorTypes.TRIAL_LIMIT:
      return 'Has alcanzado el límite de pruebas gratuitas por hoy.';
    
    case ErrorTypes.TRIAL_EXPIRED:
      return 'Tu período de prueba ha expirado.';
    
    case ErrorTypes.IP_LIMITED:
      return 'Esta dirección IP ya ha utilizado su prueba gratuita diaria.';
    
    default:
      return 'Ha ocurrido un error inesperado. Por favor, intenta de nuevo.';
  }
}

/**
 * Log an error with consistent format
 * @param {Error} error - Error object
 * @param {string} [context] - Context where the error occurred
 * @param {Object} [additionalInfo] - Additional information about the error
 */
export function logError(error, context = '', additionalInfo = {}) {
  const errorInfo = {
    message: error.message,
    type: error.type || 'unknown',
    stack: error.stack,
    context,
    ...additionalInfo
  };
  
  console.error('[ERROR]', errorInfo);
}

/**
 * Handle API response errors
 * @param {Response} response - Fetch API response
 * @returns {Promise<Error>} Error object with appropriate type
 */
export async function handleApiError(response) {
  try {
    const data = await response.json();
    
    // Map HTTP status codes to error types
    let errorType = ErrorTypes.UNKNOWN;
    
    switch (response.status) {
      case 400:
        errorType = ErrorTypes.INVALID_INPUT;
        break;
      case 401:
        errorType = ErrorTypes.AUTH;
        break;
      case 403:
        errorType = ErrorTypes.FORBIDDEN;
        break;
      case 404:
        errorType = ErrorTypes.DATA_NOT_FOUND;
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        errorType = ErrorTypes.SERVER_ERROR;
        break;
      default:
        errorType = ErrorTypes.UNKNOWN;
    }
    
    return createErrorWithType(
      errorType,
      data.message || `Error ${response.status}: ${response.statusText}`,
      { status: response.status, data }
    );
  } catch (error) {
    // If we can't parse the JSON, return a generic error
    return createErrorWithType(
      ErrorTypes.UNKNOWN,
      `Error ${response.status}: ${response.statusText}`,
      { status: response.status }
    );
  }
}

/**
 * Safely parse JSON with error handling
 * @param {string} jsonString - JSON string to parse
 * @param {*} defaultValue - Default value to return if parsing fails
 * @returns {*} Parsed JSON or default value
 */
export function safeJsonParse(jsonString, defaultValue = null) {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    logError(error, 'safeJsonParse', { jsonString });
    return defaultValue;
  }
}